import { useState } from 'react';
import { ArrowRight, Check, Compass, GraduationCap, Layers } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';

import { ScrollReveal, SplitReveal, TiltCard } from '@/components/motion';
import { PROGRAMMES, type Programme } from '@/data/programmes';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { Button, Card, CardContent, cn } from '@ibas/ui';

/** The fixed delivery modes offered per programme type — no invented options. */
const ONLINE_MODES = ['Self-Study', 'Live Online', 'Blended'] as const;
const ONSITE_MODES = ['On campus (Thalwil)'] as const;

const modesFor = (programme: Programme): readonly string[] =>
  programme.type === 'Online' ? ONLINE_MODES : ONSITE_MODES;

/** Shared chip: selected = navy fill + white text with a gold ring (never white-on-gold). */
function Chip({
  label,
  selected,
  onClick,
  sublabel,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  sublabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      data-cursor
      className={cn(
        'group relative inline-flex items-center gap-2 rounded-full border px-4 py-2 text-left transition-all duration-300 outline-none',
        'focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:transition-none',
        selected
          ? 'border-brand-navy bg-brand-navy text-white shadow-sm ring-2 ring-brand-gold'
          : 'border-border bg-background text-foreground hover:border-brand-gold/60 hover:shadow-sm',
      )}
    >
      {selected && <Check aria-hidden className="size-3.5 shrink-0 text-brand-gold-light" />}
      <span className="flex flex-col leading-tight">
        {sublabel && (
          <span
            className={cn(
              'font-label text-[0.625rem] tracking-[0.14em] uppercase',
              selected ? 'text-brand-gold-light' : 'text-muted-foreground',
            )}
          >
            {sublabel}
          </span>
        )}
        <span className="text-sm font-medium">{label}</span>
      </span>
    </button>
  );
}

/**
 * CustomTrackBuilder (/admissions) — an interactive "build your study track"
 * section. The visitor picks a programme, an optional specialisation (track then
 * option) and a study mode; a live summary card on the right reads the choices
 * back as a single route and offers an enquiry CTA. Everything selectable is
 * drawn strictly from PROGRAMMES plus the two fixed mode lists — no invented
 * programme, specialisation or fact.
 *
 * Motion: SplitReveal headline, ScrollReveal for the two columns (child stagger),
 * animated chip transitions on selection, and a subtle key-based fade on the
 * summary route as it updates. Reduced motion renders the final state, no anim.
 */
export function CustomTrackBuilder() {
  const reduced = useReducedMotion();
  const [programme, setProgramme] = useState<Programme>(PROGRAMMES[0]);
  const firstSpec = programme.specialisations?.[0];
  const [trackName, setTrackName] = useState<string | null>(firstSpec?.track ?? null);
  const [option, setOption] = useState<string | null>(firstSpec?.options[0] ?? null);
  const [mode, setMode] = useState<string>(modesFor(PROGRAMMES[0])[0]);

  // Switch programme and reset the dependent selections to sensible defaults.
  const selectProgramme = (next: Programme) => {
    setProgramme(next);
    const spec = next.specialisations?.[0];
    setTrackName(spec?.track ?? null);
    setOption(spec?.options[0] ?? null);
    setMode(modesFor(next)[0]);
  };

  // Switch track and default its option so the summary stays a complete route.
  const selectTrack = (track: string, options: readonly string[]) => {
    setTrackName(track);
    setOption(options[0] ?? null);
  };

  const modes = modesFor(programme);
  const activeTrack = programme.specialisations?.find((s) => s.track === trackName);

  // Assemble the live route, e.g. "Flexi-MBA · Innovation & Technology — AI · Blended".
  const routeParts = [
    programme.code,
    trackName && option ? `${trackName} — ${option}` : null,
    mode,
  ].filter(Boolean) as string[];
  const routeKey = routeParts.join(' · ');

  const summaryMotion = reduced
    ? {}
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
      };

  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Build your custom study track
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Shape a programme around you.
          </SplitReveal>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Choose a programme, a focus and how you want to study — and see your route take shape.
            Our advisers help you make it real.
          </p>
        </header>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
          {/* ── LEFT: the choices ─────────────────────────────────────────── */}
          <ScrollReveal from="up" stagger={0.12} className="flex flex-col gap-6">
            {/* Step 1 — Programme */}
            <fieldset className="min-w-0 rounded-2xl border border-border bg-background p-6 shadow-sm md:p-7">
              <legend className="flex items-center gap-2 font-label text-xs tracking-[0.18em] text-brand-navy uppercase">
                <Compass aria-hidden className="size-4 text-brand-gold" />
                Step 1 · Choose a programme
              </legend>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {PROGRAMMES.map((p) => (
                  <Chip
                    key={p.slug}
                    label={p.title}
                    sublabel={p.code}
                    selected={p.slug === programme.slug}
                    onClick={() => selectProgramme(p)}
                  />
                ))}
              </div>
            </fieldset>

            {/* Step 2 — Focus / specialisation */}
            <fieldset className="min-w-0 rounded-2xl border border-border bg-background p-6 shadow-sm md:p-7">
              <legend className="flex items-center gap-2 font-label text-xs tracking-[0.18em] text-brand-navy uppercase">
                <Layers aria-hidden className="size-4 text-brand-gold" />
                Step 2 · Choose your focus
              </legend>

              {programme.specialisations ? (
                <div className="mt-4 flex flex-col gap-5">
                  {/* Track picker */}
                  <div className="flex flex-wrap gap-2.5">
                    {programme.specialisations.map((spec) => (
                      <Chip
                        key={spec.track}
                        label={spec.track}
                        selected={spec.track === trackName}
                        onClick={() => selectTrack(spec.track, spec.options)}
                      />
                    ))}
                  </div>

                  {/* Option picker for the active track */}
                  {activeTrack && (
                    <div>
                      <p className="mb-2.5 text-xs text-muted-foreground">
                        Specialisations in{' '}
                        <span className="font-medium text-foreground">{activeTrack.track}</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {activeTrack.options.map((opt) => (
                          <Chip
                            key={opt}
                            label={opt}
                            selected={opt === option}
                            onClick={() => setOption(opt)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs leading-relaxed text-muted-foreground">
                    A specialisation runs subject to a minimum of 10 students (terms effective 1
                    October 2026).
                  </p>
                </div>
              ) : (
                <p className="mt-4 max-w-md rounded-lg bg-muted/50 p-4 text-sm leading-relaxed text-muted-foreground">
                  This programme is offered as a single defined route — an adviser will tailor the
                  detail with you.
                </p>
              )}
            </fieldset>

            {/* Step 3 — Study mode */}
            <fieldset className="min-w-0 rounded-2xl border border-border bg-background p-6 shadow-sm md:p-7">
              <legend className="flex items-center gap-2 font-label text-xs tracking-[0.18em] text-brand-navy uppercase">
                <GraduationCap aria-hidden className="size-4 text-brand-gold" />
                Step 3 · Choose how you study
              </legend>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {modes.map((m) => (
                  <Chip key={m} label={m} selected={m === mode} onClick={() => setMode(m)} />
                ))}
              </div>
            </fieldset>
          </ScrollReveal>

          {/* ── RIGHT: the live summary ───────────────────────────────────── */}
          <ScrollReveal from="up" delay={0.15} className="lg:sticky lg:top-24 lg:self-start">
            <TiltCard glare className="h-full">
              <Card className="h-full gap-0 overflow-hidden border-border py-0">
                <CardContent className="flex h-full flex-col p-7">
                  <p className="font-label text-xs tracking-[0.2em] text-brand-gold uppercase">
                    Your route
                  </p>

                  <motion.div key={routeKey} {...summaryMotion} className="mt-4">
                    <p className="font-display text-2xl leading-tight font-semibold text-foreground md:text-3xl">
                      {programme.title}
                    </p>
                    <div className="mt-4 space-y-3 text-sm">
                      <div className="flex items-baseline justify-between gap-4 border-b border-border pb-3">
                        <span className="font-label text-[0.625rem] tracking-[0.14em] text-muted-foreground uppercase">
                          Programme
                        </span>
                        <span className="text-right font-medium text-foreground">
                          {programme.code}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-4 border-b border-border pb-3">
                        <span className="font-label text-[0.625rem] tracking-[0.14em] text-muted-foreground uppercase">
                          Focus
                        </span>
                        <span className="text-right font-medium text-foreground">
                          {trackName && option ? `${trackName} — ${option}` : 'Adviser-tailored'}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between gap-4">
                        <span className="font-label text-[0.625rem] tracking-[0.14em] text-muted-foreground uppercase">
                          Study mode
                        </span>
                        <span className="text-right font-medium text-foreground">{mode}</span>
                      </div>
                    </div>
                  </motion.div>

                  <div className="mt-6 rounded-lg bg-brand-navy/5 p-4">
                    <p className="font-label text-[0.625rem] tracking-[0.14em] text-muted-foreground uppercase">
                      Your track
                    </p>
                    <p className="mt-1.5 text-sm leading-snug font-medium text-brand-navy">
                      {routeParts.join(' · ')}
                    </p>
                  </div>

                  <div className="mt-auto pt-6">
                    <Button asChild size="lg" variant="primary" className="w-full" data-cursor>
                      <Link to="/apply">
                        Enquire about this track
                        <ArrowRight aria-hidden className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TiltCard>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
